import React, { useState, useEffect } from 'react';
import Layout from '../../../components/layout';
import Seo from '../../../components/seo';
import '../../../styles/patent-roi-calculator.css';
import { Disc } from 'lucide-react';
import Disclaimer from '../../../components/disclaimer';

const PatentROICalculator = () => {
  const [legalCost, setLegalCost] = useState(20000);
  const [ipValuation, setIpValuation] = useState(1500000);
  const [brandValue, setBrandValue] = useState(500000);
  const [barrierValue, setBarrierValue] = useState(5000000);

  // SEO data
  const seo = {
    metaDesc: 'Patent ROI Calculator - Measure Your Patent Investment Performance & Strategic Value. Calculate returns on patent investments and understand the financial benefits of intellectual property protection.'
  };

  // Format currency function
  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return '$' + (amount / 1000000).toFixed(1) + 'M';
    } else if (amount >= 1000) {
      return '$' + (amount / 1000).toFixed(0) + 'K';
    } else {
      return '$' + amount.toLocaleString();
    }
  };

  // Format percentage function
  const formatPercentage = (value) => {
    return value.toLocaleString() + '%';
  };

  // Calculate ROI values
  const calculateROI = () => {
    const totalValue = ipValuation + brandValue + barrierValue;
    const netBenefit = totalValue - legalCost;
    const roi = ((netBenefit / legalCost) * 100);
    const valueRatio = Math.round(totalValue / legalCost);
    const breakEven = legalCost; // Simplified: annual licensing needed to break even in 1 year

    return {
      totalValue,
      netBenefit,
      roi: Math.round(roi),
      valueRatio,
      breakEven
    };
  };

  const results = calculateROI();

  // Add no-home class to body on mount
  useEffect(() => {
    document?.body.classList.add('no-home');
    return () => {
      document?.body.classList.remove('no-home');
    };
  }, []);

  return (
    // <Layout activeMenu={'ai-tools'}>
    //   <Seo 
    //     title="Patent ROI Calculator - Measure Your Patent Investment Performance" 
    //     canonical='/ai-tools/patent-roi-calculator' 
    //     seo={seo} 
    //   />
      
      <div className="patent-roi-calculator">
        <div className="patent-roi-container">
          <div className="patent-roi-header">
            <h1>Patent ROI Calculator</h1>
            <p>Measure Your Patent Investment Performance & Strategic Value</p>
          </div>

          <div className="patent-roi-calculator-container">
            <div className="patent-roi-input-section">
              <h2>Patent Investment Parameters</h2>
              
              <div className="patent-roi-input-group">
                <label htmlFor="legalCost">Legal Costs (Filing, Prosecution, Maintenance)</label>
                <div className="patent-roi-input-range-container">
                  <input 
                    type="range" 
                    id="legalCost" 
                    className="patent-roi-input-range" 
                    min="10000" 
                    max="30000" 
                    value={legalCost} 
                    step="1000"
                    onChange={(e) => setLegalCost(parseInt(e.target.value))}
                  />
                  <div className="patent-roi-range-values">
                    <span>$10,000</span>
                    <span>$30,000</span>
                  </div>
                  <div className="patent-roi-current-value">{formatCurrency(legalCost)}</div>
                </div>
              </div>

              <div className="patent-roi-input-group">
                <label htmlFor="ipValuation">IP Asset Valuation</label>
                <div className="patent-roi-input-range-container">
                  <input 
                    type="range" 
                    id="ipValuation" 
                    className="patent-roi-input-range" 
                    min="250000" 
                    max="5000000" 
                    value={ipValuation} 
                    step="50000"
                    onChange={(e) => setIpValuation(parseInt(e.target.value))}
                  />
                  <div className="patent-roi-range-values">
                    <span>$250K</span>
                    <span>$5M</span>
                  </div>
                  <div className="patent-roi-current-value">{formatCurrency(ipValuation)}</div>
                </div>
              </div>

              <div className="patent-roi-input-group">
                <label htmlFor="brandValue">Patent-Pending Brand Value</label>
                <div className="patent-roi-input-range-container">
                  <input 
                    type="range" 
                    id="brandValue" 
                    className="patent-roi-input-range" 
                    min="200000" 
                    max="1000000" 
                    value={brandValue} 
                    step="25000"
                    onChange={(e) => setBrandValue(parseInt(e.target.value))}
                  />
                  <div className="patent-roi-range-values">
                    <span>$200K</span>
                    <span>$1M</span>
                  </div>
                  <div className="patent-roi-current-value">{formatCurrency(brandValue)}</div>
                </div>
              </div>

              <div className="patent-roi-input-group">
                <label htmlFor="barrierValue">Competitive Barrier to Entry Value</label>
                <div className="patent-roi-input-range-container">
                  <input 
                    type="range" 
                    id="barrierValue" 
                    className="patent-roi-input-range" 
                    min="1000000" 
                    max="10000000" 
                    value={barrierValue} 
                    step="100000"
                    onChange={(e) => setBarrierValue(parseInt(e.target.value))}
                  />
                  <div className="patent-roi-range-values">
                    <span>$1M</span>
                    <span>$10M</span>
                  </div>
                  <div className="patent-roi-current-value">{formatCurrency(barrierValue)}</div>
                </div>
              </div>
            </div>

            <div className="patent-roi-results-section">
              <h2>Patent Investment Returns</h2>
              
              <div className="patent-roi-highlight">
                <div className="patent-roi-roi-value">{formatPercentage(results.roi)}</div>
                <div>Total Return on Investment</div>
              </div>

              <div className="patent-roi-results-grid">
                <div className="patent-roi-result-card">
                  <h3>Total Patent Value</h3>
                  <div className="patent-roi-result-value">{formatCurrency(results.totalValue)}</div>
                  <div className="patent-roi-result-description">Combined IP asset, brand, and barrier value</div>
                </div>

                <div className="patent-roi-result-card">
                  <h3>Net Patent Benefit</h3>
                  <div className="patent-roi-result-value">{formatCurrency(results.netBenefit)}</div>
                  <div className="patent-roi-result-description">Total value minus legal investment</div>
                </div>

                <div className="patent-roi-result-card">
                  <h3>Value-to-Cost Ratio</h3>
                  <div className="patent-roi-result-value">{results.valueRatio}:1</div>
                  <div className="patent-roi-result-description">Patent value per dollar invested</div>
                </div>

                <div className="patent-roi-result-card">
                  <h3>Break-Even Licensing Revenue</h3>
                  <div className="patent-roi-result-value">{formatCurrency(results.breakEven)}</div>
                  <div className="patent-roi-result-description">Annual licensing needed to recover costs</div>
                </div>
              </div>

              <div className="patent-roi-benefit-highlight">
                <h4>🚀 Strategic Benefits</h4>
                <p>Your patent provides market exclusivity for up to 20 years, creates defensible competitive moats, and signals innovation credibility to investors and partners.</p>
              </div>

              <div className="patent-roi-benefit-highlight">
                <h4>💰 Revenue Opportunities</h4>
                <p>Potential for licensing royalties ($0.05-$0.50 per unit), IP sales ($100K-$10M), and equity-for-IP deals with milestone royalties.</p>
              </div>
            </div>
          </div>

          <div className="patent-roi-framework-section">
            <h2>Patent Financial Benefit Framework</h2>
            
            <div className="patent-roi-framework-grid">
              <div className="patent-roi-framework-card">
                <h3>I. Strategic Value Proposition</h3>
                <div className="patent-roi-framework-item">
                  <strong>Market Exclusivity</strong>
                  <span>Monopoly-like control for up to 20 years</span>
                </div>
                <div className="patent-roi-framework-item">
                  <strong>Defensibility</strong>
                  <span>Creates competitive moat in crowded markets</span>
                </div>
                <div className="patent-roi-framework-item">
                  <strong>Innovation Signal</strong>
                  <span>Enhances credibility with stakeholders</span>
                </div>
              </div>

              <div className="patent-roi-framework-card">
                <h3>II. Revenue Generation Channels</h3>
                <div className="patent-roi-framework-item">
                  <strong>Licensing</strong>
                  <span>$0.05–$0.50 per unit or % of revenue</span>
                </div>
                <div className="patent-roi-framework-item">
                  <strong>Sale/Assignment</strong>
                  <span>$100K–$10M lump-sum transfers</span>
                </div>
                <div className="patent-roi-framework-item">
                  <strong>Equity-for-IP</strong>
                  <span>Stock + milestone-based royalties</span>
                </div>
              </div>

              <div className="patent-roi-framework-card">
                <h3>III. Valuation & Fundraising Impact</h3>
                <div className="patent-roi-framework-item">
                  <strong>Higher Valuation</strong>
                  <span>Patents boost intangible asset worth</span>
                </div>
                <div className="patent-roi-framework-item">
                  <strong>Investor Confidence</strong>
                  <span>Reduces risk in deep tech sectors</span>
                </div>
                <div className="patent-roi-framework-item">
                  <strong>Exit Leverage</strong>
                  <span>Acquirers pay premiums for IP-backed companies</span>
                </div>
              </div>

              <div className="patent-roi-framework-card">
                <h3>IV. Operational & Tax Efficiency</h3>
                <div className="patent-roi-framework-item">
                  <strong>R&D Tax Credits</strong>
                  <span>Patent expenses qualify for deductions</span>
                </div>
                <div className="patent-roi-framework-item">
                  <strong>Patent Box Regimes</strong>
                  <span>Lower tax rates on patented product income</span>
                </div>
                <div className="patent-roi-framework-item">
                  <strong>Cost Recovery</strong>
                  <span>Licensing offsets R&D and legal costs</span>
                </div>
              </div>

              <div className="patent-roi-framework-card">
                <h3>V. Negotiation & Strategic Leverage</h3>
                <div className="patent-roi-framework-item">
                  <strong>Cross-Licensing</strong>
                  <span>Avoid litigation, gain access to others' IP</span>
                </div>
                <div className="patent-roi-framework-item">
                  <strong>Defensive Use</strong>
                  <span>Counter-sue or negotiate settlements</span>
                </div>
                <div className="patent-roi-framework-item">
                  <strong>Partnerships</strong>
                  <span>Bargaining chips in joint ventures</span>
                </div>
              </div>
            </div>
          </div>
          <Disclaimer />
        </div>
      </div>
    // </Layout>
  );
};

export default PatentROICalculator;
